<template>
  <section
    v-if="variants.v1 === variant"
    class="n-section-primary"
    :style="`background-color: var(--c-${vars.backgroundColor})`"
  >
    <div
      class="tw-flex tw-flex-col tw-items-center tw-justify-between tw-gap-10 xl:tw-gap-24 2xl:tw-flex-row"
      :class="{ 'xl:tw-flex-row-reverse': vars.isReversedBool }"
    >
      <div class="tw-flex tw-flex-col tw-p-0">
        <div v-html="vars.titleHtml" class="tw-mb-6 tw-flex tw-gap-4 tw-text-white"></div>
        <div class="tw-flex tw-flex-col tw-gap-4">
          <p
            v-for="(item, index) in descriptions"
            :key="`ourvaluations-description-${index}`"
            class="tw-max-w-2xl !tw-text-white"
          >
            {{ item.description }}
          </p>
        </div>
      </div>
      <div class="tw-flex tw-w-auto tw-flex-col tw-justify-center tw-gap-6 md:tw-flex-row">
        <div
          v-for="(valuation, index) in valuations"
          :key="`ourvaluations-valuation-${index}`"
          class="n-card tw-flex tw-min-w-[300px] tw-max-w-xl tw-flex-[1_1_40%] tw-flex-col tw-items-center tw-justify-start tw-border-b-8 tw-border-[var(--c-primary)] tw-bg-white tw-text-black xl:tw-h-auto xl:tw-w-full xl:tw-flex-[1_1_20%]"
        >
          <LibraryImage :src="valuation.photo" :h-ratio="10" :w-ratio="16" />
          <div
            class="tw-mt-2 tw-flex tw-h-full tw-w-full tw-flex-col tw-items-start tw-justify-stretch tw-gap-5 tw-p-5 sm:tw-p-8 xl:tw-pb-2 xl:tw-pt-6"
          >
            <h4 class="tw-line-clamp-2 xl:tw-h-[60px]">
              {{ valuation.title }}
            </h4>
            <p class="tw-mb-4 tw-line-clamp-[6]">
              {{ valuation.description }}
            </p>
            <nuxt-link
              :external="true"
              class="tw-mt-auto tw-cursor-pointer !tw-whitespace-normal tw-text-center"
              :to="valuation.button_link"
              :class="`n-${valuation.button_type} tw-mb-4 tw-mt-auto`"
              >{{ valuation.button_label }}</nuxt-link
            >
          </div>
        </div>
      </div>
    </div>
  </section>

  <section
    v-else-if="variants.v2 === variant"
    class="n-section-primary"
    :style="`background-color: var(--c-${vars.backgroundColor})`"
  >
    <MotionGroup
      is="div"
      :preset="vars.animationText ? vars.animationText : 'slideVisibleBottom'"
      :duration="700"
      class="tw-flex tw-flex-col tw-items-center tw-justify-between tw-gap-10 2xl:tw-flex-row 2xl:tw-gap-24"
    >
      <div class="tw-mx-auto tw-flex tw-w-full tw-flex-col 2xl:tw-w-1/2">
        <div v-html="vars.titleHtml" class="tw-mb-6 tw-text-left"></div>
        <div class="tw-flex tw-flex-col tw-gap-3">
          <p
            v-for="(item, index) in descriptions"
            :key="`ourvaluations-description-${index}`"
            class="tw-mb-1 tw-text-left"
            :style="{ color: 'var(--c-tertiary)' }"
          >
            {{ item.description }}
          </p>
        </div>
      </div>

      <div class="tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-2 2xl:tw-w-1/2">
        <div
          v-for="(valuation, index) in valuations"
          :key="`ourvaluations-valuation-${index}`"
          class="card-box tw-overflow-hidden tw-bg-white tw-p-4"
          style="border-radius: var(--rounded)"
        >
          <div class="tw-flex tw-h-full tw-w-full tw-flex-col tw-items-center tw-gap-6 tw-border-none">
            <div class="card-photo tw-w-full tw-overflow-hidden" style="border-radius: var(--rounded)">
              <LibraryImage
                :src="valuation.photo"
                :h-ratio="1"
                :w-ratio="1.5"
                image-class="tw-w-auto tw-object-cover"
              />
            </div>
            <div
              class="tw-absolute tw-inset-0 tw-opacity-0 tw-transition-all"
              :style="`background-color: var(--c-primary);`"
            ></div>
            <div class="tw-flex tw-w-full tw-flex-grow tw-flex-col tw-gap-4">
              <h5
                class="tw-line-clamp-2 tw-w-full tw-text-left tw-text-lg tw-font-normal"
                :style="{ color: 'var(--c-secondary)' }"
              >
                {{ valuation.title }}
              </h5>
              <p class="tw-w-full tw-text-left tw-text-base tw-leading-tight" :style="{ color: 'var(--c-tertiary)' }">
                {{ valuation.description }}
              </p>
              <div class="tw-mt-auto">
                <nuxt-link
                  :external="true"
                  :to="valuation.button_link"
                  :class="`n-${valuation.button_type}`"
                  class="tw-block tw-w-full tw-py-3 tw-text-center"
                >
                  {{ valuation.button_label }}
                </nuxt-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </MotionGroup>
  </section>

  <section
    v-else-if="variants.v3 === variant"
    class="n-section-primary tw-relative tw-overflow-hidden"
    :style="`background-color: var(--c-${vars.backgroundColor})`"
  >
    <div
      v-if="company.company_key === 'townandcityhomes'"
      class="tw-pointer-events-none tw-absolute tw-bottom-0 tw-left-0 tw-hidden lg:tw-block"
    >
      <span
        ><svg width="419" height="445" viewBox="0 0 419 445" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g clip-path="url(#clip0_9_2220)">
            <path
              d="M403.2 237.4C378.6 199.8 332.1 189.3 298.1 195.6C255.7 203.5 224.3 239.5 211.1 279.9C204.7 300.1 202.9 321.4 206.5 340.8C211.1 365.1 223.6 383 233.2 394.1C234.5 395.3 234.8 396.9 234.2 397.7C233.7 398.4 232.7 398.4 232 398.3C230.3 398 229.3 396.8 228.1 395.8C226.9 394.8 225.9 393.6 224.7 392.4C222.5 390.2 220.4 387.9 218.3 385.4C214.1 380.6 210.4 375.5 206.9 370.3C203.5 365.1 200.5 359.5 197.9 353.8C195.2 348.1 193 342.3 191.2 336.3C189.4 330.3 187.9 324.2 187 318C186 311.9 185.4 305.6 185.2 299.3C185.1 293 185.4 286.7 185.9 280.4V279.9C186.7 273.8 187.8 267.6 189.3 261.6C190.1 258.6 191 255.8 191.8 252.9C194.3 245.3 198.5 238.2 201.1 230.7L137.5 167.7C71.3 195.4 66.2 197.5 0 225V429.1C0 431 0.4 432.8 1.2 434.6C3.3 440.1 8.7 444 15 444H333.8C333.6 444 333.5 443.8 333.3 443.7C326.4 439.7 309.8 428.3 301.1 407C290.2 380 295.7 345.9 318.8 324.3C323.1 320.1 328.1 316.7 333.3 314C357.9 300.5 389.8 301.7 414.9 319.1C416.8 311.9 426.4 272.7 403.2 237.2"
              fill="#2D1559"
            />
            <path
              d="M190.9 180.5C190.9 182.2 190.9 183.8 190.7 185.5H227.7C227.5 183.8 227.5 182.2 227.5 180.5C227.5 140.7 255.5 108.6 290.1 108.6C324.7 108.6 295.6 108.8 298.3 109.3V0C270.2 0 247.2 22.9 247.2 51.2H171C171 22.9 148.1 0 119.9 0V109.3C122.6 108.8 125.3 108.6 128.1 108.6C162.7 108.6 190.7 140.7 190.7 180.5"
              fill="#2D1559"
            />
          </g>
          <defs>
            <clipPath id="clip0_9_2220">
              <rect width="418.5" height="444.2" fill="white" />
            </clipPath>
          </defs>
        </svg>
      </span>
    </div>
    <MotionGroup
      is="div"
      :preset="vars.animationText ? vars.animationText : 'slideVisibleBottom'"
      :duration="750"
      class="tw-flex tw-flex-col tw-items-center tw-justify-between tw-gap-10 2xl:tw-flex-row 2xl:tw-gap-24"
    >
      <div class="tw-mx-auto tw-flex tw-w-full tw-flex-col 2xl:tw-w-1/2">
        <div class="tw-mb-6 tw-text-left">
          <h2 :style="`color: var(--c-${vars.textColor})`">{{ vars.titleText }}</h2>
        </div>
        <div class="tw-flex tw-flex-col tw-gap-3">
          <p
            v-for="(item, index) in descriptions"
            :key="`ourvaluations-description-${index}`"
            class="tw-mb-1 tw-text-left"
            :style="`color: var(--c-${vars.textColor})`"
          >
            {{ item.description }}
          </p>
        </div>
      </div>

      <div class="tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-2 lg:tw-gap-x-8 2xl:tw-w-1/2">
        <div
          v-for="(valuation, index) in valuations"
          :key="`ourvaluations-valuation-${index}`"
          class="card-box tw-p-4"
          style="border-radius: var(--rounded)"
        >
          <img
            v-if="vars.mascottEnabledBool && index === 0"
            :src="vars.mascottImage"
            alt="mascott"
            class="tw-pointer-events-none tw-absolute -tw-top-[50px] tw-left-6 tw-w-32 tw--scale-x-100 md:-tw-top-[65px] md:tw-w-[160px]"
            style="z-index: 1000"
          />
          <div
            class="tw-flex tw-h-full tw-w-full tw-flex-col tw-items-center tw-gap-6 tw-border-none"
            style="background-color: var(--c-section_tertiary)"
          >
            <div class="card-photo tw-w-full tw-overflow-hidden" style="border-radius: var(--rounded)">
              <LibraryImage
                :src="valuation.photo"
                :h-ratio="1"
                :w-ratio="1.5"
                image-class="tw-w-auto tw-object-cover"
              />
            </div>
            <div
              class="tw-absolute tw-inset-0 tw-opacity-0 tw-transition-all"
              :style="`background-color: var(--c-primary);`"
            ></div>
            <div class="tw-flex tw-w-full tw-flex-grow tw-flex-col tw-gap-4 tw-p-6">
              <h4 class="tw-line-clamp-2 tw-w-full tw-text-left" :style="`color: var(--c-${vars.textColor})`">
                {{ valuation.title }}
              </h4>
              <p class="tw-w-full tw-text-left" :style="`color: var(--c-${vars.textColor})`">
                {{ valuation.description }}
              </p>
              <div class="tw-mt-auto">
                <nuxt-link
                  :external="true"
                  :to="valuation.button_link"
                  :class="`n-${valuation.button_type}`"
                  class="tw-block tw-w-full tw-py-3 tw-text-center"
                >
                  {{ valuation.button_label }}
                </nuxt-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </MotionGroup>
  </section>
</template>

<script lang="ts">
import { ComponentMixin } from '~/mixins/component.mixin';

export default defineNuxtComponent({
  name: 'FAQSection',

  mixins: [ComponentMixin],

  data() {
    return {
      variants: {
        v1: 'v1',
        v2: 'v2',
        v3: 'v3',
      },
    };
  },

  computed: {
    valuations() {
      return this.groupedVariables.valuations;
    },

    descriptions() {
      return this.groupedVariables.descriptions;
    },

    currentFAQPhoto() {
      if (this.openIndex !== null || this.faqs[this.openIndex].photo) {
        return this.faqs[this.openIndex].photo;
      }
      return this.vars.photoImage;
    },
  },
});
</script>

<style scoped>
details[open] > summary {
  background-color: var(--c-primary) !important;
  color: var(--c-text-white) !important;
}

details > summary:hover {
  background-color: var(--c-primary) !important;
  color: var(--c-text-white) !important;
}

details {
  overflow: hidden;
}

details[open] p {
  animation: animateUp 0.5s linear forwards;
}

@keyframes animateUp {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

summary.no-arrow {
  list-style: none;
}

summary::-webkit-details-marker {
  display: none;
}

summary::before {
  content: '';
}
</style>
